import { Slot } from '@radix-ui/react-slot'
import { cn } from '@repo/ui/lib'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'
import { RefObject } from 'react'

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    defaultVariants: {
      size: 'default',
      variant: 'default',
    },
    variants: {
      size: {
        default: 'h-10 px-4 py-2',
        icon: 'h-10 w-10',
        lg: 'h-11 rounded-md px-8',
        sm: 'h-9 rounded-md px-3',
      },
      variant: {
        brandLink: 'text-brand underline-offset-4 hover:underline',
        brandOutline:
          'border border-brand bg-card hover:bg-brand/20 text-brand hover:text-brand/80',
        brandSecondary: 'bg-brand-secondary text-brand dark:text-card hover:bg-brand-secondary/80',
        default: cn(
          'text-brand-foreground hover:bg-brand/80',
          process.env.NEXT_PUBLIC_SOURCE === 'kcharles' ? 'bg-brand-gradient' : 'bg-brand',
        ),
        destructive: 'bg-red-600 text-destructive-foreground hover:bg-red-600/90',
        ghost: 'hover:bg-accent hover:text-accent-foreground text-muted-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        noStyle: ' bg-transparent',
        outline:
          'border border-input bg-background hover:bg-accent hover:text-accent-foreground text-[#637381] dark:text-[#d4d9de]',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
      },
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = ({
  asChild = false,
  className,
  ref,
  size,
  variant,
  ...props
}: ButtonProps & { ref?: ((node: HTMLElement | null) => void) | RefObject<HTMLButtonElement> }) => {
  const Comp = asChild ? Slot : 'button'
  return <Comp className={cn(buttonVariants({ className, size, variant }))} ref={ref} {...props} />
}

Button.displayName = 'Button'

export { Button, buttonVariants }
